import React, { FC } from 'react';

import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import { StorageDocumentLink } from '../../../../components/storage-document-link/index';
import { SummaryItem } from '../../../../components/summary-list/index';
import { SafeConversionSummaryResponse } from '../../../../hooks/safe/index';
import { getConversionMethodTitle, safeCapTypeTitle } from '../../../../types/safes.types';
import { toRound } from '../../../../utils/get-rounded-number.util';
import { getS3FileOriginalName } from '../../../../utils/get-s3file-original-name.util';
import { toMathRound } from '../../../../utils/to-math-round.util';
import { SafeDropDown } from './safe-drop-down';

export type ConvertSafeItemProps = {
  currencySign?: string;
  documents?: {
    abort: () => void;
    doc:
      | {
          name: string;
          size: number;
          type: string;
        }
      | File;
    docLink: string;
    id: string;
    loadProgress: number;
  }[];
  onDocumentClick?: (fileLink: string) => void;
  safeId: string;
  summary: SafeConversionSummaryResponse;
};

export const ConvertSafeItem: FC<ConvertSafeItemProps> = ({
  currencySign,
  summary: conversionSummary,
  documents,
  onDocumentClick,
}) => {
  return (
    <>
      <SafeDropDown title={conversionSummary?.stakeholder?.fullName}>
        <div className="flex w-full flex-col divide-y-[1px] divide-gray-100">
          <SummaryItem
            className="pt-0"
            title={<AppFormattedMessage id="SAFE_INVESTMENT_CONVERSION" />}
            value={`${currencySign} ${conversionSummary.investment.toLocaleString('en-US')}`}
          />
          <SummaryItem
            title={<AppFormattedMessage id="CAP_TYPE" />}
            value={
              conversionSummary.capType ? (
                <AppFormattedMessage id={safeCapTypeTitle[conversionSummary.capType]} />
              ) : (
                '-'
              )
            }
          />
          <SummaryItem
            title={<AppFormattedMessage id="VALUATION_CAP_PERCENT" />}
            value={`${toRound(conversionSummary['valuation-cap'] * 100)}%`}
          />
          <SummaryItem
            title={<AppFormattedMessage id="DISCOUNT_PERCENT" />}
            value={`${toRound(conversionSummary.discount * 100)}%`}
          />
          <SummaryItem
            title={<AppFormattedMessage id="FLOOR_PERCENT" />}
            value={`${toRound(conversionSummary.floor * 100)}%`}
          />
          <SummaryItem
            title={<AppFormattedMessage id="METHOD_OF_CONVERSION" />}
            value={
              <AppFormattedMessage
                id={getConversionMethodTitle(
                  conversionSummary.conversionMethod,
                  conversionSummary.capType,
                )}
              />
            }
          />
          <SummaryItem
            title={<AppFormattedMessage id="CONVERSION_SHARE_PRICE" />}
            value={
              conversionSummary.conversionSharePrice
                ? `${currencySign} ${toMathRound(conversionSummary.conversionSharePrice).toLocaleString('en-US')}`
                : '-'
            }
          />
          <SummaryItem
            title={<AppFormattedMessage id="PERCENT_CAPITAL_OWNED_POST_CONVERSION" />}
            value={`${toRound(conversionSummary.capitalOwned * 100)}%`}
          />
          <SummaryItem
            title={<AppFormattedMessage id="SHARES_CONVERTED" />}
            value={`${toRound(conversionSummary.sharesConverted, 0).toLocaleString('en-US')}`}
          />
          <SummaryItem
            className="pb-0"
            title={<AppFormattedMessage id="ATTACHED_FILES_TITLE" />}
            titleClassName="w-fit"
            titleWrapperClassName="w-[120px]"
            value={
              <div className="flex max-w-[220px] flex-col gap-2">
                {documents?.map((item) =>
                  typeof item === 'string' ? (
                    <StorageDocumentLink
                      key={item}
                      label={getS3FileOriginalName(item) || item}
                      link={item}
                      onDocumentClick={onDocumentClick}
                    />
                  ) : (
                    item.docLink && (
                      <StorageDocumentLink
                        key={item.docLink + item.doc.name}
                        label={item.doc.name}
                        link={item.docLink}
                        onDocumentClick={onDocumentClick}
                      />
                    )
                  ),
                )}
              </div>
            }
          />
        </div>
      </SafeDropDown>
    </>
  );
};
